import { Button, InlineStack, Select } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { NewItemsStatus } from '@/components/new-items-status';
import type { NewItemsStatusState } from '@/hooks/use-load-new';
import { filtersForTypeChange } from '../filters';
import type { ActivityTypeOptionGroup } from '../activity-log-types';
import type { ActivityLogFilters } from '../types';
import { DateRangeFilter } from './date-range-filter';
import type { NameFilterObjectType } from '../object-attribute-picker';
import { NameFilter } from './name-filter';

const NO_FILTERS: ActivityLogFilters = { type: '', name: '', from: '', to: '' };

type LogsFilterControlsProps = {
  /** Workspace id == API environment_id, for the name filters' lookups. */
  environmentId: string;
  filters: ActivityLogFilters;
  setFilters: (values: Partial<ActivityLogFilters>) => void;
  activityTypeOptions: ActivityTypeOptionGroup[];
  sentEvents: string[];
  sentAttributes: string[];
  /** On an object's own feed, the object type its object and relationship types are pinned to. */
  objectType?: NameFilterObjectType;
};

/**
 * The feed's filter controls: Activity type, the name filter that type calls for, the date range,
 * and Clear filter. Bottom-aligned: the Activity type Select carries a label above its input, so
 * the label-less controls sit on the input's baseline rather than floating up to the label row.
 */
export function LogsFilterControls({
  environmentId,
  filters,
  setFilters,
  activityTypeOptions,
  sentEvents,
  sentAttributes,
  objectType,
}: LogsFilterControlsProps) {
  const hasFilter =
    !isEmptyString(filters.type) ||
    !isEmptyString(filters.name) ||
    !isEmptyString(filters.from) ||
    !isEmptyString(filters.to);

  return (
    // InlineStack's cross-axis prop is `alignBlock` (not `alignItems`, which it silently ignores).
    <InlineStack alignBlock="flex-end" gap="100">
      <Select
        label="Activity type"
        placeholder="Select a type"
        value={isEmptyString(filters.type) ? undefined : filters.type}
        // Clear `name` when the type changes (see `filtersForTypeChange`): the name filter is
        // contextual to the type, so a carried-over name would silently filter to nothing.
        onChange={(value) => setFilters(filtersForTypeChange(value ?? ''))}
        options={activityTypeOptions}
        isSearchable
        size="sm"
      />
      {/* Remount the name filter on type change (`key`): resets the free-text input's
          local state and cancels any pending debounce, so a stale name can't be
          reapplied after the type switches (e.g. profile_create → profile_delete). */}
      <NameFilter
        key={filters.type}
        type={filters.type}
        name={filters.name}
        environmentId={environmentId}
        sentEvents={sentEvents}
        sentAttributes={sentAttributes}
        objectType={objectType}
        onChange={(name) => setFilters({ name })}
      />
      <DateRangeFilter from={filters.from} to={filters.to} onChange={(range) => setFilters(range)} />
      <Button
        variant="secondary"
        size="sm"
        icon="filter"
        isDisabled={!hasFilter}
        onClick={() => setFilters(NO_FILTERS)}
      >
        Clear filter
      </Button>
    </InlineStack>
  );
}

type LogsToolbarProps = {
  /** The controls on the left: the filters, or a note where the feed has none. */
  children: React.ReactNode;
  /** The "load new" check status, shown on the right. */
  newCheckStatus: NewItemsStatusState;
};

/** The row above the feed's table: its controls on the left, the "load new" check status on the right. */
export function LogsToolbar({ children, newCheckStatus }: LogsToolbarProps) {
  return (
    <InlineStack alignInline="space-between" alignBlock="flex-end">
      {children}
      <NewItemsStatus {...newCheckStatus} checkNowLabel="Check for new activity" />
    </InlineStack>
  );
}
